import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Terminal } from 'lucide-react';
import { trpc } from '~/api/trpc';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';

type AppointmentCartModalProps = {
    appointmentId: string;
    open: boolean;
    onClose: () => void;
};

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);

type CartItemKind = 'Bundle' | 'Bundle-Einlösung' | 'Add-on' | 'Service' | 'Variante';

// Resolve the display name and kind of a cart item following the priority:
// bundle redemption > bundle > add-on > bookable item > treatment variant.
const resolveItemDisplay = (item: any): { name: string; kind: CartItemKind } => {
    if (item.isBundleRedemption) {
        return {
            name: item.bookableItem?.nameDe ?? item.treatmentVariant?.name ?? item.bundle?.name ?? 'Bundle-Einlösung',
            kind: 'Bundle-Einlösung',
        };
    }
    if (item.bundle) {
        return { name: item.bundle.name || 'Bundle', kind: 'Bundle' };
    }
    if (item.bookableItemAddon) {
        return { name: item.bookableItemAddon.nameDe, kind: 'Add-on' };
    }
    if (item.bookableItem) {
        return { name: item.bookableItem.nameDe, kind: 'Service' };
    }
    return { name: item.treatmentVariant?.name ?? 'Unbekannt', kind: 'Variante' };
};

// Base gross price (EUR) of a cart item before discounts. Prefers the stored price
// snapshot; falls back to live variant/bundle prices only for legacy carts.
const getItemBasePrice = (item: any): number => {
    if (item.totalGross != null) {
        return Number(item.totalGross);
    }
    if (item.bundle && item.isBundleRedemption) {
        // Redemption: only a single unit of the bundle is charged.
        return Number(item.bundle.unitPriceGross);
    }
    if (item.bundle) {
        return Number(item.bundle.unitPriceGross) * item.bundle.unitQuantity;
    }
    return (item.treatmentVariant?.priceCents ?? 0) / 100;
};

// Total discount (EUR) applied to a cart item. Prefers the stored snapshot.
const getItemDiscountTotal = (item: any): number => {
    if (item.grossDiscountAmount != null) {
        return Number(item.grossDiscountAmount);
    }
    return item.discounts.reduce(
        (sum: number, d: any) => sum + (d.discountValue != null ? Number(d.discountValue) : 0),
        0
    );
};

// Discounted line total (EUR). Prefers the stored snapshot.
const getItemTotal = (item: any): number => {
    if (item.totalGrossDiscounted != null) {
        return Number(item.totalGrossDiscounted);
    }
    return Math.max(0, getItemBasePrice(item) - getItemDiscountTotal(item));
};

function AppointmentCartModal({ appointmentId, open, onClose }: AppointmentCartModalProps) {
    const cartQuery = trpc.carts.getByAppointment.useQuery(
        { appointmentId },
        { enabled: open === true }
    );

    const cart = cartQuery.data;
    const items = cart?.items ?? [];

    const subtotal = items.reduce((sum, item) => sum + getItemBasePrice(item), 0);
    const totalDiscount = items.reduce((sum, item) => sum + getItemDiscountTotal(item), 0);
    const total = items.reduce((sum, item) => sum + getItemTotal(item), 0);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Warenkorb</DialogTitle>
                    <DialogDescription>
                        {cart ? (
                            <>
                                Warenkorb-ID: <span className="font-mono">{cart.id}</span>
                            </>
                        ) : (
                            <>Warenkorb für diesen Termin</>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="p-1">
                    {cartQuery.isError && (
                        <Alert variant="destructive" className="mb-4">
                            <Terminal className="h-4 w-4" />
                            <AlertTitle>Fehler beim Laden des Warenkorbs</AlertTitle>
                            <AlertDescription>
                                {cartQuery.error?.message ?? 'Ein unbekannter Fehler ist aufgetreten.'}
                            </AlertDescription>
                        </Alert>
                    )}

                    {cartQuery.isLoading && (
                        <div className="space-y-3">
                            <Skeleton className="h-16 w-full" />
                            <Skeleton className="h-16 w-full" />
                            <Skeleton className="h-16 w-full" />
                        </div>
                    )}

                    {!cartQuery.isLoading && !cartQuery.isError && !cart && (
                        <div className="text-center text-muted-foreground py-8">
                            Kein Warenkorb für diesen Termin gefunden.
                        </div>
                    )}

                    {!cartQuery.isLoading && cart && items.length === 0 && (
                        <div className="text-center text-muted-foreground py-8">
                            Dieser Warenkorb enthält keine Positionen.
                        </div>
                    )}

                    {cart && items.length > 0 && (
                        <div className="rounded-md border divide-y max-h-[55vh] overflow-auto">
                            {items.map((item) => {
                                const { name, kind } = resolveItemDisplay(item);
                                const basePrice = getItemBasePrice(item);
                                const itemDiscountTotal = getItemDiscountTotal(item);
                                const itemTotal = getItemTotal(item);

                                return (
                                    <div key={item.id} className="p-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 shrink-0">
                                                    {kind}
                                                </Badge>
                                                <span className="text-sm font-medium truncate">{name}</span>
                                                {item.quantity > 1 && (
                                                    <span className="text-xs text-muted-foreground shrink-0">
                                                        x{item.quantity}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-sm shrink-0">{formatCurrency(basePrice)}</span>
                                        </div>

                                        {item.discounts.length > 0 && (
                                            <div className="mt-2 pl-2 space-y-1">
                                                {item.discounts.map((discount) => {
                                                    const codeName =
                                                        discount.discountCode?.name ?? discount.discountCodeName;
                                                    const amount =
                                                        discount.discountValue != null
                                                            ? Number(discount.discountValue)
                                                            : 0;
                                                    return (
                                                        <div
                                                            key={discount.id}
                                                            className="flex items-center justify-between gap-3 text-xs text-green-700"
                                                        >
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <span className="truncate">
                                                                    Rabatt: {codeName}
                                                                </span>
                                                                {discount.type && (
                                                                    <Badge
                                                                        variant="outline"
                                                                        className="text-[10px] px-1.5 py-0 h-4 shrink-0 border-green-300 text-green-700"
                                                                    >
                                                                        {discount.type === 'PERCENTAGE_DISCOUNT'
                                                                            ? 'Prozentual'
                                                                            : 'Absolut'}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <span className="shrink-0">
                                                                -{formatCurrency(amount)}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {itemDiscountTotal > 0 && (
                                            <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                                                <span>Positionssumme</span>
                                                <span>{formatCurrency(itemTotal)}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {cart && items.length > 0 && (
                        <div className="mt-4 space-y-1">
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                                <span>Zwischensumme</span>
                                <span>{formatCurrency(subtotal)}</span>
                            </div>
                            {totalDiscount > 0 && (
                                <div className="flex items-center justify-between text-sm text-green-700">
                                    <span>Rabatt gesamt</span>
                                    <span>-{formatCurrency(totalDiscount)}</span>
                                </div>
                            )}
                            <div className="flex items-center justify-between text-base font-semibold border-t pt-2">
                                <span>Gesamt</span>
                                <span>{formatCurrency(total)}</span>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Schließen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default AppointmentCartModal;
